import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

// Simple dev admin panel - no RBAC required, just authentication
// Access via /dev-admin

interface Agent {
  agent_id: string;
  name: string;
  description: string;
  instructions: string;
  tool_sets: string[];
  sample_questions: string[];
  icon?: {
    base: string;
    accessories?: {
      hat?: string;
      glasses?: string;
      shirt?: string;
      accessory?: string;
      background_color?: string;
    };
  };
}

interface AccessoryOption {
  id: string;
  name: string;
  emoji: string;
}

interface Accessories {
  hats: AccessoryOption[];
  glasses: AccessoryOption[];
  shirts: AccessoryOption[];
  accessories: AccessoryOption[];
  background_colors: { id: string; name: string }[];
}

const API_BASE = process.env.REACT_APP_API_URL || '';

export const DevAdmin: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [accessories, setAccessories] = useState<Accessories | null>(null);
  const [toolSets, setToolSets] = useState<{id: string; description: string; tools: string[]}[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [mode, setMode] = useState<'list' | 'edit' | 'create' | 'test'>('list');
  
  // Form state
  const [formData, setFormData] = useState<Partial<Agent>>({
    agent_id: '',
    name: '',
    description: '',
    instructions: '',
    tool_sets: [],
    sample_questions: [],
    icon: { base: 'aldo', accessories: {} }
  });
  
  // Test state
  const [testMessage, setTestMessage] = useState('');
  const [testResponse, setTestResponse] = useState<string | null>(null);
  const [testLoading, setTestLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [agentsRes, accessoriesRes, toolSetsRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/agents`, { credentials: 'include' }),
        fetch(`${API_BASE}/api/admin/accessories`, { credentials: 'include' }),
        fetch(`${API_BASE}/api/admin/tool-sets`, { credentials: 'include' })
      ]);
      
      if (agentsRes.ok) {
        const data = await agentsRes.json();
        setAgents(data.agents || []);
      }
      if (accessoriesRes.ok) {
        setAccessories(await accessoriesRes.json());
      }
      if (toolSetsRes.ok) {
        const data = await toolSetsRes.json();
        setToolSets(data.tool_sets || []);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, fetchData]);

  const handleSave = async () => {
    try {
      const url = mode === 'create' 
        ? `${API_BASE}/api/admin/agents`
        : `${API_BASE}/api/admin/agents/${selectedAgent?.agent_id}`;
      
      const response = await fetch(url, {
        method: mode === 'create' ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) throw new Error('Failed to save');
      
      await fetchData();
      setMode('list');
      setSelectedAgent(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    }
  };

  const handleDelete = async (agentId: string) => {
    if (!window.confirm(`Delete ${agentId}?`)) return;
    
    try {
      await fetch(`${API_BASE}/api/admin/agents/${agentId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const handleTest = async () => {
    if (!selectedAgent || !testMessage) return;
    
    setTestLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/agents/${selectedAgent.agent_id}/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: testMessage })
      });
      const data = await response.json();
      setTestResponse(data.response || data.error || JSON.stringify(data));
    } catch (err) {
      setTestResponse(`Error: ${err}`);
    } finally {
      setTestLoading(false);
    }
  };

  const handleReload = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/reload`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      alert(`Reloaded: ${data.agents_loaded?.join(', ') || 'none'}`);
      await fetchData();
    } catch (err) {
      setError('Reload failed');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl mb-4">🔐 Sign in required</h1>
          <p className="text-slate-400">Please sign in to access Dev Admin</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-green-400 to-cyan-400 bg-clip-text text-transparent">
              🛠️ Dev Admin
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              Signed in as: {(user as any)?.email || (user as any)?.userDetails || 'Unknown'}
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => navigate('/chat')} className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm">
              ← Back to Chat
            </button>
            <button onClick={handleReload} className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm">
              🔄 Reload
            </button>
            <button 
              onClick={() => { setMode('create'); setFormData({ agent_id: '', name: '', description: '', instructions: '', tool_sets: [], sample_questions: [], icon: { base: 'aldo', accessories: {} } }); }}
              className="px-3 py-2 bg-green-600 hover:bg-green-500 rounded-lg text-sm"
            >
              + New Agent
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-900/50 border border-red-500 rounded-lg text-sm">
            {error} <button onClick={() => setError(null)} className="ml-2 underline">dismiss</button>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          {/* Agent List */}
          <div className="col-span-1 bg-slate-800 rounded-xl p-4">
            <h2 className="font-semibold mb-3">Agents ({agents.length})</h2>
            {loading ? (
              <p className="text-slate-400">Loading...</p>
            ) : (
              <div className="space-y-2">
                {agents.map(agent => (
                  <div 
                    key={agent.agent_id}
                    onClick={() => { setSelectedAgent(agent); setMode('list'); }}
                    className={`p-3 rounded-lg cursor-pointer ${selectedAgent?.agent_id === agent.agent_id ? 'bg-cyan-600/30 border border-cyan-500' : 'bg-slate-700/50 hover:bg-slate-700'}`}
                  >
                    <div className="font-medium">{agent.name}</div>
                    <div className="text-xs text-slate-400">{agent.agent_id}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Detail/Edit Panel */}
          <div className="col-span-2 bg-slate-800 rounded-xl p-6">
            {mode === 'create' || mode === 'edit' ? (
              <>
                <h2 className="text-xl font-semibold mb-4">{mode === 'create' ? 'Create Agent' : `Edit ${selectedAgent?.name}`}</h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-slate-400">Agent ID</label>
                      <input
                        value={formData.agent_id || ''}
                        onChange={e => setFormData({ ...formData, agent_id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                        disabled={mode === 'edit'}
                        className="w-full mt-1 px-3 py-2 bg-slate-700 rounded-lg disabled:opacity-50"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-slate-400">Display Name</label>
                      <input
                        value={formData.name || ''}
                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                        className="w-full mt-1 px-3 py-2 bg-slate-700 rounded-lg"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-slate-400">Description</label>
                    <input
                      value={formData.description || ''}
                      onChange={e => setFormData({ ...formData, description: e.target.value })}
                      className="w-full mt-1 px-3 py-2 bg-slate-700 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400">Instructions</label>
                    <textarea
                      value={formData.instructions || ''}
                      onChange={e => setFormData({ ...formData, instructions: e.target.value })}
                      className="w-full mt-1 px-3 py-2 bg-slate-700 rounded-lg h-32 font-mono text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400">Tool Sets</label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {toolSets.map(ts => (
                        <label key={ts.id} className={`px-3 py-1 rounded-full cursor-pointer ${formData.tool_sets?.includes(ts.id) ? 'bg-cyan-600' : 'bg-slate-700'}`}>
                          <input
                            type="checkbox"
                            checked={formData.tool_sets?.includes(ts.id)}
                            onChange={e => {
                              const sets = e.target.checked 
                                ? [...(formData.tool_sets || []), ts.id]
                                : (formData.tool_sets || []).filter(s => s !== ts.id);
                              setFormData({ ...formData, tool_sets: sets });
                            }}
                            className="hidden"
                          />
                          {ts.id}
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  {/* Icon Builder */}
                  {accessories && (
                    <div>
                      <label className="text-sm text-slate-400">Icon</label>
                      <div className="grid grid-cols-4 gap-3 mt-2">
                        <select
                          value={formData.icon?.accessories?.hat || 'none'}
                          onChange={e => setFormData({ ...formData, icon: { ...formData.icon!, accessories: { ...formData.icon?.accessories, hat: e.target.value } } })}
                          className="bg-slate-700 rounded px-2 py-1"
                        >
                          {accessories.hats.map(h => <option key={h.id} value={h.id}>{h.emoji} {h.name}</option>)}
                        </select>
                        <select
                          value={formData.icon?.accessories?.glasses || 'none'}
                          onChange={e => setFormData({ ...formData, icon: { ...formData.icon!, accessories: { ...formData.icon?.accessories, glasses: e.target.value } } })}
                          className="bg-slate-700 rounded px-2 py-1"
                        >
                          {accessories.glasses.map(g => <option key={g.id} value={g.id}>{g.emoji} {g.name}</option>)}
                        </select>
                        <select
                          value={formData.icon?.accessories?.shirt || 'none'}
                          onChange={e => setFormData({ ...formData, icon: { ...formData.icon!, accessories: { ...formData.icon?.accessories, shirt: e.target.value } } })}
                          className="bg-slate-700 rounded px-2 py-1"
                        >
                          {accessories.shirts.map(s => <option key={s.id} value={s.id}>{s.emoji} {s.name}</option>)}
                        </select>
                        <select
                          value={formData.icon?.accessories?.accessory || 'none'}
                          onChange={e => setFormData({ ...formData, icon: { ...formData.icon!, accessories: { ...formData.icon?.accessories, accessory: e.target.value } } })}
                          className="bg-slate-700 rounded px-2 py-1"
                        >
                          {accessories.accessories.map(a => <option key={a.id} value={a.id}>{a.emoji} {a.name}</option>)}
                        </select>
                      </div>
                      <div className="flex gap-2 mt-2">
                        {accessories.background_colors.map(c => (
                          <button
                            key={c.id}
                            onClick={() => setFormData({ ...formData, icon: { ...formData.icon!, accessories: { ...formData.icon?.accessories, background_color: c.id } } })}
                            className={`w-8 h-8 rounded-full border-2 ${formData.icon?.accessories?.background_color === c.id ? 'border-white' : 'border-transparent'}`}
                            style={{ backgroundColor: c.id }}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3 pt-4">
                    <button onClick={() => setMode('list')} className="px-4 py-2 bg-slate-700 rounded-lg">Cancel</button>
                    <button onClick={handleSave} className="px-4 py-2 bg-green-600 rounded-lg">Save</button>
                  </div>
                </div>
              </>
            ) : mode === 'test' && selectedAgent ? (
              <>
                <h2 className="text-xl font-semibold mb-4">Test {selectedAgent.name}</h2>
                <div className="flex gap-2 mb-4">
                  <input
                    value={testMessage}
                    onChange={e => setTestMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 px-3 py-2 bg-slate-700 rounded-lg"
                    onKeyDown={e => e.key === 'Enter' && handleTest()}
                  />
                  <button onClick={handleTest} disabled={testLoading} className="px-4 py-2 bg-cyan-600 rounded-lg disabled:opacity-50">
                    {testLoading ? '...' : 'Send'}
                  </button>
                </div>
                {testResponse && (
                  <div className="p-4 bg-slate-700/50 rounded-lg">
                    <pre className="whitespace-pre-wrap text-sm">{testResponse}</pre>
                  </div>
                )}
                <button onClick={() => setMode('list')} className="mt-4 text-sm text-slate-400 underline">← Back</button>
              </>
            ) : selectedAgent ? (
              <>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h2 className="text-2xl font-bold">{selectedAgent.name}</h2>
                    <p className="text-slate-400">{selectedAgent.agent_id}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => { setMode('test'); setTestMessage(''); setTestResponse(null); }} className="px-3 py-1.5 bg-cyan-600 rounded-lg text-sm">🧪 Test</button>
                    <button onClick={() => { setMode('edit'); setFormData(selectedAgent); }} className="px-3 py-1.5 bg-blue-600 rounded-lg text-sm">✏️ Edit</button>
                    <button onClick={() => handleDelete(selectedAgent.agent_id)} className="px-3 py-1.5 bg-red-600 rounded-lg text-sm">🗑️</button>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm text-slate-400">Description</h3>
                    <p>{selectedAgent.description}</p>
                  </div>
                  <div>
                    <h3 className="text-sm text-slate-400">Tool Sets</h3>
                    <div className="flex gap-2">
                      {selectedAgent.tool_sets?.map(ts => (
                        <span key={ts} className="px-2 py-1 bg-cyan-600/30 rounded text-sm">{ts}</span>
                      ))}
                      {(!selectedAgent.tool_sets || selectedAgent.tool_sets.length === 0) && <span className="text-slate-500">None</span>}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm text-slate-400">Sample Questions</h3>
                    <ul className="list-disc list-inside text-sm">
                      {selectedAgent.sample_questions?.map((q, i) => <li key={i}>{q}</li>)}
                    </ul>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-12 text-slate-400">
                <div className="text-5xl mb-4">🤖</div>
                <p>Select an agent or create a new one</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DevAdmin;
